/**
 * wizard-api.ts — Lệnh gọi API của luồng tạo nhân viên AI (`/ai/nhan-vien/moi`):
 *  - POST /ai/bots (tạo bản nháp: tắt, chưa giao kênh — `201 {bot, warnings}`), PUT /ai/bots/:id, GET /ai/bots/:id;
 *  - GET /ai/bots/:id/status (danh sách kiểm tra ra mắt), POST /ai/bots/:id/publish;
 *  - POST /ai/scenarios { botIds:[id] } (kỹ năng gợi ý của mẫu — chỉ áp cho nhân viên này), GET /ai/scenarios?botId=.
 * Bỏ tạm dừng toàn bộ AI dùng `useSetAiPausedAll` (use-ai-khung.ts) → PUT /ai/config { autoReplyEnabled: true }.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { aiKeys, createScenario, type AiBot, type AiBotInput, type AiBotStatus, type ScenarioMeta } from '@/hooks/use-ai'
import type { BotEffective } from '@/lib/ai-credits'
import type { BotSettings } from '../train/bot-settings'
import { skillKey, type WizardSkill, type WizardTemplateKey } from './wizard-templates'

/** Bot như GET /ai/bots/:id trả (kèm `settings`). */
export type WizardBot = AiBot & { settings?: Partial<BotSettings> | null }

export type WizardBotInput = AiBotInput & { settings?: Partial<BotSettings> }

export type WizardBotStatus = AiBotStatus & { botId?: string; effective?: BotEffective | null }

export const wizardKeys = {
  bot: (id: string | null): unknown[] => ['ai', 'bots', 'one', id],
  status: (id: string | null): unknown[] => ['ai', 'bots', 'status', id],
  skills: (id: string | null): unknown[] => ['ai', 'scenarios', 'bot', id],
}

export function useWizardBot(id: string | null) {
  return useQuery<WizardBot>({
    queryKey: wizardKeys.bot(id),
    enabled: !!id,
    queryFn: async () => (await api.get<{ bot: WizardBot }>(`/ai/bots/${id}`)).data.bot,
    retry: false,
    // Bản nháp có thể được sửa ở màn chi tiết (mở ở tab mới) → quay lại tab là nạp bản mới.
    refetchOnWindowFocus: true,
  })
}

export function useBotStatus(id: string | null, enabled = true) {
  return useQuery<WizardBotStatus>({
    queryKey: wizardKeys.status(id),
    enabled: !!id && enabled,
    queryFn: async () => (await api.get<WizardBotStatus>(`/ai/bots/${id}/status`)).data,
    // Mục kiểm tra ra mắt sửa ở màn khác (tab mới) → quay lại tab là kiểm tra lại.
    refetchOnWindowFocus: true,
  })
}

export function useBotSkills(id: string | null) {
  return useQuery<ScenarioMeta[]>({
    queryKey: wizardKeys.skills(id),
    enabled: !!id,
    queryFn: async () => (await api.get<{ scenarios: ScenarioMeta[] }>('/ai/scenarios', { params: { botId: id } })).data.scenarios ?? [],
  })
}

export async function createWizardBot(input: WizardBotInput) {
  const { data } = await api.post<{ bot: WizardBot; warnings?: string[] }>('/ai/bots', input)
  return data
}

export async function updateWizardBot(id: string, input: WizardBotInput) {
  const { data } = await api.put<{ bot: WizardBot; warnings?: string[] }>(`/ai/bots/${id}`, input)
  return data
}

export async function publishWizardBot(id: string, note = 'Ra mắt từ luồng tạo nhân viên AI') { // i18n-ignore: ghi chú phiên bản LƯU (tiếng Việt)
  const { data } = await api.post<{ version: { id: string; version: number } }>(`/ai/bots/${id}/publish`, { note })
  return data.version
}

/** Tạo các kỹ năng gợi ý của mẫu, chỉ áp cho nhân viên AI này. Lỗi một kỹ năng không chặn các kỹ năng khác. */
export async function createTemplateSkills(botId: string, template: WizardTemplateKey, skills: WizardSkill[]): Promise<{ created: number; failed: number }> {
  const res = await Promise.allSettled(skills.map((s, i) => createScenario({
    key: skillKey(template, i, botId),
    name: s.name,
    description: s.description,
    content: s.content,
    loadMode: 'auto',
    enabled: true,
    botIds: [botId],
  })))
  return { created: res.filter((r) => r.status === 'fulfilled').length, failed: res.filter((r) => r.status === 'rejected').length }
}

/** Làm mới mọi dữ liệu liên quan tới một bot (bản ghi, trạng thái, danh sách). */
export function useInvalidateBot() {
  const qc = useQueryClient()
  return (id: string | null) => Promise.all([
    qc.invalidateQueries({ queryKey: wizardKeys.bot(id) }),
    qc.invalidateQueries({ queryKey: wizardKeys.status(id) }),
    qc.invalidateQueries({ queryKey: aiKeys.bots, exact: true }),
  ])
}
